import { useState, useEffect, useCallback, useRef } from 'react';
import styles from '../../page.module.css';

type ResizeEdge =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

interface WindowSize {
  width: number;
  height: number;
}

interface WindowPosition {
  x: number;
  y: number;
}

interface UseWindowResizeOptions {
  minWidth?: number;
  minHeight?: number;
  computeDefaults?: (desktopWidth: number, desktopHeight: number) => {
    size: WindowSize;
    position: WindowPosition;
  };
}

interface UseWindowResizeReturn {
  size: WindowSize;
  position: WindowPosition;
  setPosition: (pos: WindowPosition) => void;
  isResizing: boolean;
  handleResizeStart: (edge: ResizeEdge) => (e: React.PointerEvent) => void;
}

export function useWindowResize(
  initialSize: WindowSize,
  initialPosition: WindowPosition,
  options: UseWindowResizeOptions = {}
): UseWindowResizeReturn {
  const {
    minWidth = 200,
    minHeight = 150,
  } = options;

  const [size, setSize] = useState<WindowSize>(initialSize);
  const [position, setPosition] = useState<WindowPosition>(initialPosition);
  const [isResizing, setIsResizing] = useState(false);

  // Track current values in refs for ResizeObserver callback
  const sizeRef = useRef(size);
  const positionRef = useRef(position);
  useEffect(() => { sizeRef.current = size; }, [size]);
  useEffect(() => { positionRef.current = position; }, [position]);

  const resizeState = useRef<{
    edge: ResizeEdge;
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
    startPosX: number;
    startPosY: number;
  } | null>(null);

  // Compute responsive defaults on first observation, then constrain on resize
  const computeDefaultsRef = useRef(options.computeDefaults);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    const desktopEl = document.querySelector(`.${styles.desktop}`) as HTMLElement;
    if (!desktopEl) return;

    const handleDesktopResize = () => {
      const desktopRect = desktopEl.getBoundingClientRect();
      const desktopWidth = desktopRect.width;
      const dockHeight = 56;
      const desktopHeight = desktopRect.height - dockHeight;

      // On first observation, compute responsive defaults if available
      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
        const computeDefaults = computeDefaultsRef.current;
        if (computeDefaults) {
          const defaults = computeDefaults(desktopWidth, desktopHeight);
          defaults.size.width = Math.max(minWidth, defaults.size.width);
          defaults.size.height = Math.max(minHeight, defaults.size.height);
          sizeRef.current = defaults.size;
          positionRef.current = defaults.position;
          setSize(defaults.size);
          setPosition(defaults.position);
          return;
        }
      }

      // Subsequent observations: constrain to bounds
      const currentSize = sizeRef.current;
      const currentPos = positionRef.current;

      let newWidth = currentSize.width;
      let newHeight = currentSize.height;
      let newX = currentPos.x;
      let newY = currentPos.y;

      // If window is wider than desktop, shrink it
      if (newWidth > desktopWidth) {
        newWidth = Math.max(minWidth, desktopWidth);
      }
      // If window is taller than desktop, shrink it
      if (newHeight > desktopHeight) {
        newHeight = Math.max(minHeight, desktopHeight);
      }

      // Push window left if it's going off the right edge
      if (newX + newWidth > desktopWidth) {
        newX = Math.max(0, desktopWidth - newWidth);
      }
      // Push window up if it's going off the bottom edge
      if (newY + newHeight > desktopHeight) {
        newY = Math.max(0, desktopHeight - newHeight);
      }

      // Update size if changed
      if (newWidth !== currentSize.width || newHeight !== currentSize.height) {
        setSize({ width: newWidth, height: newHeight });
      }
      // Update position if changed
      if (newX !== currentPos.x || newY !== currentPos.y) {
        setPosition({ x: newX, y: newY });
      }
    };

    const resizeObserver = new ResizeObserver(handleDesktopResize);
    resizeObserver.observe(desktopEl);

    return () => {
      resizeObserver.disconnect();
    };
  }, [minWidth, minHeight]);

  const handleResizeStart = useCallback(
    (edge: ResizeEdge) => (e: React.PointerEvent) => {
      e.stopPropagation();
      e.preventDefault();

      resizeState.current = {
        edge,
        startX: e.clientX,
        startY: e.clientY,
        startWidth: size.width,
        startHeight: size.height,
        startPosX: position.x,
        startPosY: position.y,
      };
      setIsResizing(true);
    },
    [size, position]
  );

  useEffect(() => {
    if (!isResizing) return;

    const handlePointerMove = (e: PointerEvent) => {
      if (!resizeState.current) return;

      // Get desktop bounds
      const desktopEl = document.querySelector(`.${styles.desktop}`) as HTMLElement;
      if (!desktopEl) return;
      const desktopRect = desktopEl.getBoundingClientRect();
      const desktopWidth = desktopRect.width;
      const dockHeight = 56;
      const desktopHeight = desktopRect.height - dockHeight;

      const { edge, startX, startY, startWidth, startHeight, startPosX, startPosY } =
        resizeState.current;

      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      let newWidth = startWidth;
      let newHeight = startHeight;
      let newX = startPosX;
      let newY = startPosY;

      // Handle horizontal resizing
      if (edge.includes('right')) {
        // Can't extend past desktop right edge
        const maxAllowedWidth = desktopWidth - startPosX;
        newWidth = Math.min(maxAllowedWidth, Math.max(minWidth, startWidth + deltaX));
      }
      if (edge.includes('left')) {
        const possibleWidth = startWidth - deltaX;
        const possibleX = startPosX + deltaX;

        // Can't go past left edge (x < 0)
        if (possibleX < 0) {
          newX = 0;
          newWidth = Math.max(minWidth, startWidth + startPosX);
        } else if (possibleWidth >= minWidth) {
          newWidth = possibleWidth;
          newX = possibleX;
        } else {
          newWidth = minWidth;
          newX = startPosX + (startWidth - minWidth);
        }
      }

      // Handle vertical resizing
      if (edge.includes('bottom')) {
        // Can't extend past desktop bottom edge
        const maxAllowedHeight = desktopHeight - startPosY;
        newHeight = Math.min(maxAllowedHeight, Math.max(minHeight, startHeight + deltaY));
      }
      if (edge.includes('top')) {
        const possibleHeight = startHeight - deltaY;
        const possibleY = startPosY + deltaY;

        // Can't go past top edge (y < 0)
        if (possibleY < 0) {
          newY = 0;
          newHeight = Math.max(minHeight, startHeight + startPosY);
        } else if (possibleHeight >= minHeight) {
          newHeight = possibleHeight;
          newY = possibleY;
        } else {
          newHeight = minHeight;
          newY = startPosY + (startHeight - minHeight);
        }
      }

      // Final bounds check - ensure window stays within desktop
      newX = Math.max(0, Math.min(newX, desktopWidth - newWidth));
      newY = Math.max(0, Math.min(newY, desktopHeight - newHeight));
      newWidth = Math.min(newWidth, desktopWidth - newX);
      newHeight = Math.min(newHeight, desktopHeight - newY);

      setSize({ width: newWidth, height: newHeight });
      setPosition({ x: newX, y: newY });
    };

    const handlePointerUp = () => {
      resizeState.current = null;
      setIsResizing(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [isResizing, minWidth, minHeight]);

  return {
    size,
    position,
    setPosition,
    isResizing,
    handleResizeStart,
  };
}
